import { BBDTag, Horizontal, MaterialIcon, type MaterialSymbolIcon, Texto } from '@gravitate-js/excalibrr'
import { MinusOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'

import type { InvoiceDetail } from '../../../../../api/useFreight/types'

/**
 * Configuration for line item status display
 */
export const LINE_ITEM_STATUS_CONFIG: Record<
  string,
  {
    display: string
    icon: string
    tagProps: {
      error?: boolean
      warning?: boolean
      success?: boolean
      theme2?: boolean
    }
  }
> = {
  error: {
    display: 'Error',
    icon: 'error',
    tagProps: { error: true, warning: false, success: false },
  },
  needs_approval: {
    display: 'Needs Approval',
    icon: 'warning',
    tagProps: { error: false, warning: true, success: false },
  },
  ready: {
    display: 'Ready',
    icon: 'check_circle',
    tagProps: { error: false, warning: false, success: false, theme2: true },
  },
  estimated: {
    display: 'Estimated',
    icon: 'tactic',
    tagProps: { error: false, warning: false, success: false },
  },
  manual: {
    display: 'Manual',
    icon: 'edit',
    tagProps: { error: false, warning: false, success: false, theme2: true },
  },
  posted: {
    display: 'Posted',
    icon: 'lock',
    tagProps: { error: false, warning: false, success: true },
  },
}

interface StatusCellRendererProps {
  data: InvoiceDetail
  isRebillMode?: boolean
}

/**
 * Renders a status tag with appropriate icon and styling
 */
export function StatusCellRenderer({ data, isRebillMode = false }: StatusCellRendererProps): JSX.Element {
  const rebillStatusConfig: Record<
    string,
    {
      icon: React.ReactNode
      success?: boolean
      error?: boolean
      warning?: boolean
      theme2?: boolean
    }
  > = {
    added: { icon: <PlusOutlined className='pr-2' />, success: true },
    removed: { icon: <MinusOutlined className='pr-2' />, error: true },
    impacted: { icon: <SyncOutlined className='pr-2' />, warning: true },
    unchanged: { icon: null },
  }

  if (!isRebillMode) {
    const config = LINE_ITEM_STATUS_CONFIG?.[data.status as keyof typeof LINE_ITEM_STATUS_CONFIG]

    if (!config) {
      return <Texto>{data.status}</Texto>
    }

    return (
      <BBDTag
        error={config.tagProps.error}
        warning={config.tagProps.warning}
        success={config.tagProps.success}
        theme2={config.tagProps.theme2}
        style={{ width: 'fit-content' }}
      >
        <Horizontal verticalCenter gap={4}>
          <MaterialIcon icon={config.icon as MaterialSymbolIcon} />
          {config.display}
        </Horizontal>
      </BBDTag>
    )
  }

  const status = data.credit_rebill_metadata?.impact_type || 'unchanged'
  const statusProps = rebillStatusConfig[status as keyof typeof rebillStatusConfig]

  return (
    <BBDTag
      success={statusProps?.success}
      warning={statusProps?.warning}
      theme2={statusProps?.theme2}
      error={statusProps?.error}
      className='mr-0'
      style={{ width: 'fit-content', textTransform: 'capitalize' }}
    >
      {statusProps?.icon}
      {status}
    </BBDTag>
  )
}
